import React from 'react';
import PropTypes from 'prop-types';
import { Box, Text } from 'grommet';
import styled from 'styled-components';

const FilterBadge = styled(Box)`
  border-radius: var(--app-radius);
`;

const ClearAllButton = styled(Box)`
  cursor: pointer;
  border-radius: var(--app-radius);
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    opacity: 0.9;
  }
`;

// Constants for filter types
const RANGE_FILTER_NAMES = ['start_date', 'end_date', 'result_count'];
const GTE_PREDICATES = ['gte', 'gteq'];
const LTE_PREDICATES = ['lte', 'lteq'];

const ActiveFilters = ({
  filters,
  onClearAll,
  primaryText,
  focusHighlight,
}) => {
  if (!filters || filters.length === 0) return null;

  // Group filters (no useMemo needed - this calculation is fast)
  const groupedFilters = (() => {
    const grouped = [];
    const processedIndices = new Set();

    filters.forEach((filter, index) => {
      if (processedIndices.has(index)) return;

      const isRangeFilter = RANGE_FILTER_NAMES.includes(filter.name);

      if (isRangeFilter) {
      // Find matching range filter (gte/lte pair)
        const otherIndex = filters.findIndex((f, i) => (
          i > index
        && f.name === filter.name
        && f.predicate !== filter.predicate
        ));

        if (otherIndex !== -1) {
        // Found a pair - group them
          const gteFilter = GTE_PREDICATES.includes(filter.predicate)
            ? filter
            : filters[otherIndex];
          const lteFilter = LTE_PREDICATES.includes(filter.predicate)
            ? filter
            : filters[otherIndex];

          grouped.push({
            ...filter,
            isGrouped: true,
            gteValue: gteFilter.value,
            lteValue: lteFilter.value,
            indices: [index, otherIndex],
          });
          processedIndices.add(index);
          processedIndices.add(otherIndex);
        } else {
        // Single range filter
          grouped.push({ ...filter, isGrouped: false, indices: [index] });
          processedIndices.add(index);
        }
      } else {
      // Non-range filter
        grouped.push({ ...filter, isGrouped: false, indices: [index] });
        processedIndices.add(index);
      }
    });

    return grouped;
  })();


  // Map filter names to readable labels
  const getFilterLabel = (filter) => {
    /* eslint-disable camelcase */
    const labelMap = {
      status: 'Status',
      platforms: 'Platform',
      name: 'Search',
      unit: 'Unit',
      start_date: 'Start Date',
      end_date: 'End Date',
      result_count: 'Results',
    };
    /* eslint-enable camelcase */

    return labelMap[filter.name] || filter.name;
  };

  // Format filter value for display
  const getFilterValue = (filter) => {
    const {
      name, value, predicate, isGrouped, gteValue, lteValue,
    } = filter;

    // Handle grouped ranges
    if (isGrouped) {
      return `${gteValue} to ${lteValue}`;
    }

    // Handle arrays (like platforms)
    if (Array.isArray(value)) {
      return value.join(', ');
    }

    // Handle single range filters for dates
    const isDateFilter = ['start_date', 'end_date'].includes(name);
    const isResultCountFilter = name === 'result_count';

    if (isDateFilter || isResultCountFilter) {
      if (GTE_PREDICATES.includes(predicate)) {
        return isDateFilter ? `from ${value}` : `≥ ${value}`;
      }
      if (LTE_PREDICATES.includes(predicate)) {
        return isDateFilter ? `to ${value}` : `≤ ${value}`;
      }
    }

    return value;
  };

  return (
    <Box
      direction="row"
      align="center"
      wrap
      gap="small"
      pad={{ horizontal: 'medium', vertical: 'small' }}
      background="var(--page-alt-bg)"
      round="small"
    >
      <Text size="0.9rem" color={primaryText} weight={600}>
        Active Filters:
      </Text>
      {groupedFilters.map((filter) => {
        // Create unique key using indices instead of stringifying complex values
        const filterKey = `filter-${filter.indices.join('-')}`;
        return (
          <FilterBadge
            key={filterKey}
            direction="row"
            align="center"
            pad="0.3rem 0.5rem"
            background="var(--page-input-bg)"
            border={{ color: focusHighlight, size: '1px' }}
          >
            <Text size="0.85rem" color={primaryText}>
              <Text size="0.85rem" weight={600}>
                {getFilterLabel(filter)}
                :
              </Text>
              {' '}
              {getFilterValue(filter)}
            </Text>
          </FilterBadge>
        );
      })}
      {filters.length > 0 && (
        <ClearAllButton
          pad="0.3rem 0.5rem"
          background="var(--button-primary-bg)"
          onClick={onClearAll}
        >
          <Text size="0.85rem" color="var(--button-primary-text)" weight={600}>
            Clear All
          </Text>
        </ClearAllButton>
      )}
    </Box>
  );
};

ActiveFilters.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
      predicate: PropTypes.string.isRequired,
    }),
  ),
  onClearAll: PropTypes.func.isRequired,
  primaryText: PropTypes.string.isRequired,
  focusHighlight: PropTypes.string.isRequired,
};

export default ActiveFilters;

